import { call, put, select, takeLatest } from 'redux-saga/effects';

import { Api } from '../../../../../entries/ApiTransport';
import sagaAssessor from '../../../../../utils/sagaAssessor';
import { expensesTypes, expensesActions } from './actions';
import {
  mapApiExpenseToUi,
  mapUiExpenseToCreatePayload,
  mapUiExpenseToUpdatePayload,
} from '../apiMappers';
import {
  getDemoChairRevenue,
  getDemoExpenses,
  getDemoExpensesTrend,
} from 'demo';
import { getLiveDemoBundle, selectIsDemoMode } from 'demo/saga';
import { addDemoExpense, markExpenseDeleted, nextDemoId } from 'demo/session';

const api = Api.getInstance();
const skipGlobalLoader = { skipLoader: true };

const getExpenses = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const month = payload.month;
        const force = Boolean(payload.force);
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          const expenses = getDemoExpenses(bundle, month).map(mapApiExpenseToUi);
          yield put(expensesActions.GET_EXPENSES.SUCCESS({ month, expenses }));
          callback?.();
          return;
        }
        const cached = yield select(
          (state) => state.expensesReducer.expensesByMonth?.[month],
        );

        if (cached && !force) {
          callback?.();
          return;
        }

        const params = new URLSearchParams({ month });
        if (payload.offset != null) params.set('offset', String(payload.offset));
        if (payload.limit != null) params.set('limit', String(payload.limit));

        const { data } = yield call(() =>
          api.get(`/business/spese/all?${params.toString()}`),
        );
        const expenses = (data?.expenses || []).map(mapApiExpenseToUi);
        yield put(expensesActions.GET_EXPENSES.SUCCESS({ month, expenses }));
        callback?.();
      },
    (err) => expensesActions.GET_EXPENSES.FAILED(err),
    { ...skipGlobalLoader, ...options },
  );

const getTrend = ({ payload, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const months = payload?.months || 6;
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(
            expensesActions.GET_TREND.SUCCESS({ trend: getDemoExpensesTrend(bundle, months) }),
          );
          return;
        }
        const { data } = yield call(() =>
          api.get(`/business/spese/trend?months=${months}`),
        );
        yield put(expensesActions.GET_TREND.SUCCESS({ trend: data || [] }));
      },
    (err) => expensesActions.GET_TREND.FAILED(err),
    { ...skipGlobalLoader, ...options },
  );

const getChairRevenue = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          callback?.(getDemoChairRevenue(bundle, payload.idPoltrona));
          return;
        }
        const params = new URLSearchParams({
          idPoltrona: String(payload.idPoltrona),
          month: payload.month,
        });
        const { data } = yield call(() =>
          api.get(`/business/spese/chair-revenue?${params.toString()}`),
        );
        callback?.(data);
      },
    (err) => {
      callback?.(null);
      return expensesActions.GET_CHAIR_REVENUE.FAILED(err);
    },
    skipGlobalLoader,
  );

const createExpense = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const body = mapUiExpenseToCreatePayload(payload.expense, payload.chairs);
        if (yield* selectIsDemoMode()) {
          const month = String(payload.expense?.data || '').slice(0, 7);
          const created = {
            ...body,
            idExpense: nextDemoId(),
            date: payload.expense?.data,
            paid: payload.expense?.stato === 'pagato' ? 1 : 0,
          };
          addDemoExpense(month, created);
          yield put(
            expensesActions.CREATE_EXPENSE.SUCCESS({
              expenses: [created].map(mapApiExpenseToUi),
            }),
          );
          callback?.();
          return;
        }
        const { data } = yield call(() => api.post('/business/spese/create', body));
        const expenses = (data?.expenses || []).map(mapApiExpenseToUi);
        yield put(expensesActions.CREATE_EXPENSE.SUCCESS({ expenses }));
        callback?.();
      },
    (err) => expensesActions.CREATE_EXPENSE.FAILED(err),
  );

const updateExpense = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const body = mapUiExpenseToUpdatePayload(
          payload.expense,
          payload.changes,
          payload.chairs,
        );
        if (yield* selectIsDemoMode()) {
          const updated = {
            ...payload.expense,
            ...payload.changes,
          };
          yield put(expensesActions.UPDATE_EXPENSE.SUCCESS({ expense: updated }));
          callback?.();
          return;
        }
        yield call(() => api.post('/business/spese/update', body));
        const updated = {
          ...payload.expense,
          ...payload.changes,
        };
        yield put(expensesActions.UPDATE_EXPENSE.SUCCESS({ expense: updated }));
        callback?.();
      },
    (err) => expensesActions.UPDATE_EXPENSE.FAILED(err),
  );

const deleteExpense = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          markExpenseDeleted(payload.idExpense);
          yield put(expensesActions.DELETE_EXPENSE.SUCCESS({ id: payload.idExpense }));
          callback?.();
          return;
        }
        yield call(() =>
          api.post('/business/spese/delete', { idExpense: payload.idExpense }),
        );
        yield put(expensesActions.DELETE_EXPENSE.SUCCESS({ id: payload.idExpense }));
        callback?.();
      },
    (err) => expensesActions.DELETE_EXPENSE.FAILED(err),
  );

const deleteRecurringExpense = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          markExpenseDeleted(payload.idExpense);
          yield put(expensesActions.DELETE_RECURRING_EXPENSE.SUCCESS());
          callback?.();
          return;
        }
        yield call(() =>
          api.post('/business/spese/delete-recurring', { idExpense: payload.idExpense }),
        );
        yield put(expensesActions.DELETE_RECURRING_EXPENSE.SUCCESS());
        callback?.();
      },
    (err) => expensesActions.DELETE_RECURRING_EXPENSE.FAILED(err),
  );

export default function* () {
  yield takeLatest(expensesTypes.GET_EXPENSES.REQUEST, getExpenses);
  yield takeLatest(expensesTypes.GET_TREND.REQUEST, getTrend);
  yield takeLatest(expensesTypes.GET_CHAIR_REVENUE.REQUEST, getChairRevenue);
  yield takeLatest(expensesTypes.CREATE_EXPENSE.REQUEST, createExpense);
  yield takeLatest(expensesTypes.UPDATE_EXPENSE.REQUEST, updateExpense);
  yield takeLatest(expensesTypes.DELETE_EXPENSE.REQUEST, deleteExpense);
  yield takeLatest(expensesTypes.DELETE_RECURRING_EXPENSE.REQUEST, deleteRecurringExpense);
}
